package screens.post

import androidx.compose.runtime.*
import com.example.readingnotes.di.AppModule
import com.example.readingnotes.presentation.postlist.PostListEvent
import com.example.readingnotes.presentation.postlist.PostListViewModel
import com.example.readingnotes.presentation.postlist.SortOption
import com.example.readingnotes.presentation.theme.ThemeState
import com.example.readingnotes.presentation.theme.ThemeViewModel
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import styles.AppStyleSheet
import utils.SseClient
import utils.WebPushManager
import utils.getItem
import utils.setItem
import org.w3c.notifications.NotificationPermission
import com.example.readingnotes.domain.usecase.GetNotificationsUseCase
import components.NotificationPermissionPrompt

@Composable
fun PostListScreen(
    onPostClick: (Int) -> Unit,
    onAdminClick: () -> Unit,
    isLoggedIn: Boolean = false,
    onLogoutClick: () -> Unit = {},
    appModule: AppModule,
    appScope: CoroutineScope,
    themeViewModel: ThemeViewModel,
    onNotificationHistoryClick: () -> Unit = {},
    onNotificationSettingsClick: () -> Unit = {},
) {
    val themeState by themeViewModel.state.collectAsState()
    val viewModel =
        remember {
            PostListViewModel(
                appModule.loadPostListUseCase,
                appModule.loadPostListWithPaginationUseCase,
                appModule.searchPostsUseCase,
                appModule.loadPopularTagsUseCase,
                appScope
            )
        }
    val state by viewModel.state.collectAsState()

    // SSE client for real-time updates
    val apiBaseUrl = js("window.API_BASE_URL || 'http://localhost:3001'") as String
    val sseClient = remember { SseClient.create(apiBaseUrl) }

    // Push notification state
    var pushNotificationEnabled by remember { mutableStateOf(false) }
    var pushNotificationSupported by remember { mutableStateOf(false) }
    val webPushManager = remember { WebPushManager(apiBaseUrl) }

    // Unread notification count state
    var unreadCount by remember { mutableStateOf(0) }

    // Notification permission prompt state
    var showPermissionPrompt by remember { mutableStateOf(false) }
    val permissionPromptShownKey = "notification_permission_prompt_shown"

    // Check if permission prompt was shown before
    val promptWasShown = remember {
        getItem(permissionPromptShownKey)?.toBoolean() ?: false
    }

    // Check push notification support and permission on mount
    LaunchedEffect(Unit) {
        pushNotificationSupported = webPushManager.isSupported()
        val permission = js("Notification.permission") as String
        pushNotificationEnabled = permission == "granted"

        // Fetch unread notification count
        appModule.getNotificationsUseCase(limit = 100, offset = 0, unreadOnly = true)
            .onSuccess { paginatedNotifications ->
                unreadCount = paginatedNotifications.total
            }
            .onFailure { error ->
                console.error("Failed to fetch unread count:", error)
            }

        // Show permission prompt if:
        // 1. Push notifications are supported
        // 2. Prompt hasn't been shown before
        // 3. Permission is not yet determined (default)
        if (pushNotificationSupported && !promptWasShown && permission == "default") {
            kotlinx.coroutines.delay(1000) // Small delay for better UX
            showPermissionPrompt = true
        }
    }

    // Handle push notification toggle
    fun togglePushNotifications() {
        if (pushNotificationEnabled) {
            // Unsubscribe
            appScope.launch {
                webPushManager.unsubscribe(
                    onSuccess = {
                        pushNotificationEnabled = false
                        console.log("Unsubscribed from push notifications")
                    },
                    onError = { error ->
                        console.error("Failed to unsubscribe:", error)
                    }
                )
            }
        } else {
            // Subscribe
            appScope.launch {
                webPushManager.subscribe(
                    onSuccess = {
                        pushNotificationEnabled = true
                        console.log("Subscribed to push notifications")
                    },
                    onError = { error ->
                        console.error("Failed to subscribe:", error)
                    }
                )
            }
        }
    }

    LaunchedEffect(Unit) {
        viewModel.onEvent(PostListEvent.OnAppear)

        // Set up SSE event listeners
        sseClient.addEventListener("post.created") { event ->
            console.log("SSE: Post created event received", event.data)
            // Reload posts to get the latest list
            viewModel.onEvent(PostListEvent.OnAppear)
        }

        sseClient.addEventListener("post.deleted") { event ->
            console.log("SSE: Post deleted event received", event.data)
            // Reload posts to get the latest list
            viewModel.onEvent(PostListEvent.OnAppear)
        }

        sseClient.addEventListener("comment.created") { event ->
            console.log("SSE: Comment created event received", event.data)
        }

        // Connect to SSE
        sseClient.connect()
    }

    // Cleanup SSE connection on dispose
    DisposableEffect(Unit) {
        onDispose {
            sseClient.disconnect()
        }
    }

    // Infinite scroll implementation - temporary disabled for compatibility
    /*
    DisposableEffect(Unit) {
        val handleScroll: (Event) -> Unit = {
            if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 100) {
                // Near bottom of page
                if (state.hasMore && !state.isLoadingMore && !state.isLoading) {
                    viewModel.onEvent(PostListEvent.OnLoadMore)
                }
            }
        }

        window.addEventListener("scroll", handleScroll)
        onDispose {
            window.removeEventListener("scroll", handleScroll)
        }
    }
    */

    Div({
        classes(AppStyleSheet.header)
        attr("data-testid", "mobile-nav")
    }) {
        H1 { Text("読書メモ") }
        Div({ classes(AppStyleSheet.headerButtons) }) {
            // Notification history button with badge
            Button({
                classes(AppStyleSheet.iconButton)
                onClick { onNotificationHistoryClick() }
                attr("title", "通知履歴")
                style {
                    position(Position.Relative)
                }
            }) {
                Text("📋")
                if (unreadCount > 0) {
                    Span({
                        classes(AppStyleSheet.notificationBadge)
                    }) {
                        Text(if (unreadCount > 99) "99+" else unreadCount.toString())
                    }
                }
            }

            // Notification settings button
            Button({
                classes(AppStyleSheet.iconButton)
                onClick { onNotificationSettingsClick() }
                attr("title", "通知設定")
            }) {
                Text("⚙️")
            }

            // Push notification toggle button
            if (pushNotificationSupported) {
                Button({
                    classes(AppStyleSheet.iconButton)
                    onClick { togglePushNotifications() }
                    attr("title", if (pushNotificationEnabled) "プッシュ通知: オン" else "プッシュ通知: オフ")
                    style {
                        if (pushNotificationEnabled) {
                            backgroundColor(Color("#4CAF50"))
                        }
                    }
                }) {
                    Text(if (pushNotificationEnabled) "🔔" else "🔕")
                }
            }

            // Theme toggle button
            Button({
                classes(AppStyleSheet.iconButton)
                onClick { themeViewModel.toggleTheme() }
                attr("title", if (themeState.isDarkMode) "ライトモード" else "ダークモード")
                attr("data-testid", "theme-toggle")
            }) {
                Text(if (themeState.isDarkMode) "☀️" else "🌙")
            }
            if (isLoggedIn) {
                // Single "管理" button for logged-in users (consolidates admin functions)
                Button({
                    classes(AppStyleSheet.primaryButton)
                    onClick { onAdminClick() }
                }) { Text("管理") }
            } else {
                // Admin login button for non-logged-in users
                Button({
                    classes(AppStyleSheet.secondaryButton)
                    onClick { onAdminClick() }
                }) { Text("管理者ログイン") }
            }
        }
    }

    // Search and filter section
    Div({ classes(AppStyleSheet.searchSection) }) {
        // Search bar
        Div({ classes(AppStyleSheet.searchBar) }) {
            Input(type = InputType.Text) {
                classes(AppStyleSheet.searchInput)
                attr("placeholder", "検索...")
                attr("data-testid", "search-input")
                value(state.searchQuery)
                onInput { viewModel.onEvent(PostListEvent.OnSearchQueryChanged(it.value)) }
            }
            if (state.searchQuery.isNotBlank()) {
                Button({
                    classes(AppStyleSheet.clearSearchButton)
                    onClick { viewModel.onEvent(PostListEvent.OnClearSearch) }
                }) { Text("クリア") }
            }
        }

        // Sort and filter controls
        Div({ classes(AppStyleSheet.controlsBar) }) {
            // Sort dropdown
            Div({ classes(AppStyleSheet.controlGroup) }) {
                Span({ classes("control-label") }) { Text("並び替え:") }
                Select({
                    classes(AppStyleSheet.selectInput)
                    attr("value", when (state.sortBy) {
                        SortOption.NEWEST -> "newest"
                        SortOption.OLDEST -> "oldest"
                        SortOption.TITLE_ASC -> "title_asc"
                        SortOption.TITLE_DESC -> "title_desc"
                    })
                    onChange { event ->
                        val sortBy = when (event.target.asDynamic().value) {
                            "oldest" -> SortOption.OLDEST
                            "title_asc" -> SortOption.TITLE_ASC
                            "title_desc" -> SortOption.TITLE_DESC
                            else -> SortOption.NEWEST
                        }
                        viewModel.onEvent(PostListEvent.OnSortChanged(sortBy))
                    }
                }) {
                    Option("newest") { Text("新しい順") }
                    Option("oldest") { Text("古い順") }
                    Option("title_asc") { Text("タイトル (A-Z)") }
                    Option("title_desc") { Text("タイトル (Z-A)") }
                }
            }
        }
    }

    when {
        state.isLoading && state.posts.isEmpty() && !state.isSearchActive -> Div({ classes(AppStyleSheet.loading) }) { Text("読み込み中...") }
        state.errorMessage != null && state.posts.isEmpty() && !state.isSearchActive -> {
            Div({ classes(AppStyleSheet.error) }) {
                Text(state.errorMessage!!)
                Button({
                    classes(AppStyleSheet.primaryButton)
                    style {
                        marginTop(16.px)
                    }
                    onClick { viewModel.onEvent(PostListEvent.OnRetry) }
                }) { Text("リトライ") }
            }
        }
        else -> {
            // Determine which posts to display
            val displayPosts = if (state.isSearchActive) {
                state.searchResults
            } else {
                state.posts
            }

            // Show search result count if searching
            if (state.isSearchActive) {
                Div({
                    style {
                        padding(8.px, 0.px)
                        color(Color("#666"))
                        fontSize(14.px)
                    }
                }) {
                    Text("${displayPosts.size}件の検索結果 (${state.searchQuery})")
                }
            }

            Div({
                classes(AppStyleSheet.postList)
                attr("data-testid", "post-list")
            }) {
                if (displayPosts.isEmpty()) {
                    if (state.isSearchActive) {
                        Div({
                            style {
                                padding(40.px, 20.px)
                                textAlign("center")
                                color(Color("#999"))
                            }
                            attr("data-testid", "no-results")
                        }) {
                            Text("検索結果が見つかりませんでした")
                        }
                    } else {
                        Div({
                            style {
                                padding(40.px, 20.px)
                                textAlign("center")
                                color(Color("#999"))
                            }
                        }) {
                            Text("投稿がまだありません")
                        }
                    }
                } else {
                    displayPosts.forEach { post ->
                        Div({
                            classes(AppStyleSheet.postCard)
                            onClick { onPostClick(post.id) }
                            attr("data-testid", "post-item")
                        }) {
                            H2 { Text(post.title) }
                            Div({
                                classes(AppStyleSheet.postContent)
                            }) {
                                val preview = post.contentMarkdown
                                    .take(100)
                                    .replace("\n", " ")
                                    .replace(Regex("\\[.*?\\]\\(.*?\\)"), "画像") // Replace markdown images
                                Text(if (preview.length > 100) "$preview..." else preview)
                            }
                        }
                    }
                }

                // Load more button or loading indicator (only for non-search)
                if (!state.isSearchActive && state.hasMore) {
                    if (state.isLoadingMore) {
                        Div({ classes(AppStyleSheet.loading) }) { Text("読み込み中...") }
                    } else {
                        Div({ style {
                            padding(20.px, 0.px)
                            textAlign("center")
                        } }) {
                            Button({
                                classes(AppStyleSheet.secondaryButton)
                                onClick { viewModel.onEvent(PostListEvent.OnLoadMore) }
                            }) {
                                Text("もっと読み込む")
                            }
                        }
                    }
                } else if (!state.isSearchActive && state.posts.isNotEmpty()) {
                    Div({
                        style {
                            padding(20.px, 0.px)
                            textAlign("center")
                            color(Color("#666"))
                        }
                    }) {
                        Text("すべての投稿を読み込みました")
                    }
                }
            }
        }
    }

    // Notification permission prompt
    NotificationPermissionPrompt(
        isVisible = showPermissionPrompt,
        onEnable = {
            // User clicked "Enable notifications"
            showPermissionPrompt = false
            setItem(permissionPromptShownKey, "true")
            // Subscribe to push notifications
            appScope.launch {
                webPushManager.subscribe(
                    onSuccess = {
                        pushNotificationEnabled = true
                        console.log("Subscribed to push notifications")
                    },
                    onError = { error ->
                        console.error("Failed to subscribe:", error)
                    }
                )
            }
        },
        onLater = {
            // User clicked "Later"
            showPermissionPrompt = false
            setItem(permissionPromptShownKey, "true")
        }
    )
}
